import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, output, signal } from '@angular/core';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterProductSearch } from '../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { SuppliersService } from '../../../../../services/suppliers/suppliers.service';
import {
  SupplierFilters,
  SupplierOrderBy,
  SupplierOrderDir,
  DEFAULT_SUPPLIER_FILTERS,
} from './supplier-filters-component.model';

type TitleResult = { id: number; titleAr: string; titleEn: string };

@Component({
  selector: 'app-supplier-filters-component',
  imports: [FilterProductSearch, FilterDateComponent, FilterSortingComponent],
  templateUrl: './supplier-filters-component.html',
  styleUrl: './supplier-filters-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierFiltersComponent {
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private suppliersService = inject(SuppliersService);
  private searchTimeout: ReturnType<typeof setTimeout> | undefined;

  applyFilters = output<SupplierFilters>();
  resetFilters = output<void>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  supplierTitle = signal('');
  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  orderBy = signal<SupplierOrderBy>(DEFAULT_SUPPLIER_FILTERS.orderBy);
  orderDir = signal<SupplierOrderDir>(DEFAULT_SUPPLIER_FILTERS.orderDir);

  titleResults = signal<TitleResult[]>([]);
  titleLoading = signal(false);
  titleError = signal('');
  showTitleDropdown = signal(false);

  createdDateInvalid = signal(false);

  sortOptions = [
    { value: 'TITLE', labelEn: 'Supplier', labelAr: 'المورد' },
    { value: 'CREATION_DATE', labelEn: 'Created Date', labelAr: 'تاريخ الإنشاء' },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.searchTimeout));
  }

  onTitleSearchChange(value: string) {
    clearTimeout(this.searchTimeout);
    this.titleError.set('');

    if (!value) {
      this.titleResults.set([]);
      this.titleLoading.set(false);
      this.showTitleDropdown.set(false);
      return;
    }

    this.showTitleDropdown.set(true);
    this.titleLoading.set(true);
    this.searchTimeout = setTimeout(() => this.searchTitles(value), 400);
  }

  onTitleSelect(item: TitleResult) {
    this.supplierTitle.set(item.titleEn);
    this.showTitleDropdown.set(false);
  }

  onToggleSortOrder() {
    this.orderDir.update((dir) => (dir === 'DESC' ? 'ASC' : 'DESC'));
    this.onApplyFilters();
  }

  onApplyFilters() {
    const createdInvalid = this.isInvalidRange(this.createdOnFrom(), this.createdOnTo());
    this.createdDateInvalid.set(createdInvalid);
    if (createdInvalid) return;

    this.applyFilters.emit({
      supplierTitle: this.supplierTitle(),
      createdOnFrom: this.createdOnFrom(),
      createdOnTo: this.createdOnTo(),
      orderBy: this.orderBy(),
      orderDir: this.orderDir(),
    });
  }

  onResetFilters() {
    clearTimeout(this.searchTimeout);
    this.supplierTitle.set('');
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.orderBy.set(DEFAULT_SUPPLIER_FILTERS.orderBy);
    this.orderDir.set(DEFAULT_SUPPLIER_FILTERS.orderDir);
    this.titleResults.set([]);
    this.showTitleDropdown.set(false);
    this.createdDateInvalid.set(false);
    this.resetFilters.emit();
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  private isInvalidRange(from: string | null, to: string | null): boolean {
    return !!from && !!to && from > to;
  }

  private searchTitles(term: string) {
    const subscription = this.suppliersService.getAllSuppliers({ supplierTitle: term }).subscribe({
      next: (res) => {
        this.titleResults.set(
          (res.list ?? []).map((c) => ({ id: c.id, titleAr: c.titleAr ?? c.titleEn, titleEn: c.titleEn })),
        );
        this.titleLoading.set(false);
      },
      error: () => {
        this.titleResults.set([]);
        this.titleLoading.set(false);
        this.titleError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results');
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
